import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import { getVesselEmissionFuelEfficiency } from "../../../services/vesselEmissionService";
import { generateDateTicks } from "../../../services/dataTicks";

const FuelParameterChart = ({ voyageId }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const [fuelEfficiency, setFuelEfficiency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedParam, setSelectedParam] = useState("Total MT/Day");

  useEffect(() => {
    const fetchFuelEfficiency = async () => {
      if (!currentVessel?._id) {
        console.log("No vessel selected, skipping fetch");
        return;
      }

      if (!voyageId && (!startDate || !endDate)) {
        console.log(
          "No voyage selected and missing date range, skipping fetch"
        );
        return;
      }

      console.log("Fetching emission overview with:", {
        vesselId: currentVessel._id,
        voyageId,
        startDate: voyageId ? null : startDate,
        endDate: voyageId ? null : endDate,
      });

      setLoading(true);
      try {
        const response = await getVesselEmissionFuelEfficiency(
          currentVessel._id,
          voyageId ? null : startDate,
          voyageId ? null : endDate,
          voyageId
        );

        console.log("Response:", response);

        if (response?.status === "error") {
          setError("Failed to fetch fuel efficiency data");
          setFuelEfficiency(null);
        } else {
          setFuelEfficiency(response.data || response);
          setError(null);
          if (response.data?.parameters) {
            const params = Object.keys(response.data.parameters);
            setSelectedParam(params[0] || "Total MT/Day");
          }
        }
      } catch (err) {
        setError(err.message);
        console.error("API Error:", err);
        setFuelEfficiency(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFuelEfficiency();
  }, [currentVessel, startDate, endDate, voyageId]);

  const getAvailableParameters = () => {
    if (!fuelEfficiency?.parameters) return [];
    return Object.keys(fuelEfficiency.parameters);
  };

  const safeValue = (val, maxThreshold = 10000) => {
    const num = Number(val);
    return isFinite(num) && Math.abs(num) < maxThreshold ? num : 0;
  };

  // Generate chart data
  const getChartData = () => {
    if (!fuelEfficiency?.dates || !fuelEfficiency.parameters?.[selectedParam]) {
      return { seriesData: [], tickPositions: [] };
    }

    const tickPositions = generateDateTicks(fuelEfficiency.dates);

    const seriesData = [
      {
        name: "Total MT/Day",
        type: "line",
        data: fuelEfficiency.dates.map((date, i) => ({
          x: new Date(date).getTime(),
          y: safeValue(fuelEfficiency.parameters[selectedParam]?.average[i]),
          min: safeValue(fuelEfficiency.parameters[selectedParam]?.min[i]),
          max: safeValue(fuelEfficiency.parameters[selectedParam]?.max[i]),
        })),
        color: "#1a5276",
        lineWidth: 3,
        marker: {
          symbol: "circle",
          radius: 5,
          lineWidth: 1,
          lineColor: "#fff",
          enabled: true,
        },
        zIndex: 2,
        tooltip: {
          pointFormat:
            '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
        },
      },
    ];

    // Add target line if target exists
    const target = fuelEfficiency.parameters[selectedParam]?.target;
    if (target && target > 0) {
      seriesData.push({
        name: "Target",
        type: "line",
        data: fuelEfficiency.dates.map((date) => [
          new Date(date).getTime(),
          target,
        ]),
        color: "#e74c3c",
        lineWidth: 2,
        dashStyle: "Dash",
        marker: { enabled: false },
        zIndex: 3,
        tooltip: {
          pointFormat:
            '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
        },
      });
    }

    return { seriesData, tickPositions };
  };

  const { seriesData, tickPositions } = getChartData();

  const chartOptions = {
    chart: {
      type: "line",
      zoomType: "xy",
      backgroundColor: "transparent",
      height: 400,
    },
    title: {
      text: null,
    },
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: { text: null },
      labels: { format: "{value:%b %e}" },
      gridLineWidth: 1,
    },
    yAxis: {
      title: {
        text: selectedParam,
        style: {
          fontWeight: "bold",
        },
      },
      min: 0,
      minPadding: 0,
      maxPadding: 0.05,
      tickAmount: 6,
      gridLineWidth: 1,
      startOnTick: true,
      endOnTick: true,
    },
    tooltip: {
      useHTML: true,
      shared: true,
      headerFormat: "<b>{point.key:%b %e}</b><br>",
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
    },
    series: seriesData,
    credits: { enabled: false },
    plotOptions: {
      series: {
        states: {
          hover: {
            halo: {
              size: 5,
              opacity: 0.25,
            },
          },
          inactive: {
            opacity: 1,
          },
        },
      },
      line: {
        cursor: "pointer",
        point: {
          events: {
            mouseOver: function () {
              this.series.chart.tooltip.refresh(this);
            },
          },
        },
        minPointLength: 1,
      }
    },
  };

  if (loading) {
    return (
      <div className="p-4 text-center">Loading fuel efficiency data...</div>
    );
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">{error}</div>;
  }

  if (!fuelEfficiency) {
    return (
      <div className="p-4 text-center text-gray-500">
        No fuel efficiency data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center rounded-tl-2xl rounded-tr-2xl">
          <h2 className="text-lg font-semibold text-gray-800">
            Fuel Efficiency
          </h2>
        </div>
      </div>

      <div className="px-6">
        {/* Simple title above the graph */}
        <div className="mb-4">
          <div className="flex items-center">
            <div className="w-1 h-6 bg-[#1a5276] rounded-full mr-3"></div>
            <h3 className="text-lg font-semibold text-gray-800">
              Total MT/Day
            </h3>
          </div>
        </div>

        {fuelEfficiency.parameters && getAvailableParameters().length > 0 ? (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        ) : (
          <div className="text-center py-8 text-gray-500">
            No parameter data available
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelParameterChart;
